import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { setToken, clearToken, getToken, apiCall } from '../utils/api';

export interface Member {
  id: number;
  username: string;
  displayName: string;
  instrument: string;
  isAdmin: boolean;
  defaultPage?: string | null;
}

// Where a logged-in member should land. Falls back to the old role-based
// behaviour when they have no landing page set.
export function landingPageFor(member: Member | null): string {
  if (!member) return '/gig/login';
  return member.defaultPage || (member.isAdmin ? '/gig/queue' : '/gig/band/dashboard');
}

interface AuthContextType {
  member: Member | null;
  isLoggedIn: boolean;
  isAdmin: boolean;
  landingPage: string;
  login: (member: Member, token: string) => void;
  logout: () => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

const STORAGE_KEY = 'rodeoke_member';
const COOKIE_KEY = 'rodeoke_member';

// Cookie helpers — 1 year expiry, never auto-expires
function setCookie(key: string, value: string) {
  const expires = new Date(Date.now() + 365 * 24 * 60 * 60 * 1000).toUTCString();
  document.cookie = `${key}=${encodeURIComponent(value)};expires=${expires};path=/;SameSite=Lax`;
}

function getCookie(key: string): string | null {
  const match = document.cookie.match(new RegExp(`(?:^|; )${key}=([^;]*)`));
  return match ? decodeURIComponent(match[1]) : null;
}

function deleteCookie(key: string) {
  document.cookie = `${key}=;expires=Thu, 01 Jan 1970 00:00:00 GMT;path=/;SameSite=Lax`;
}

function loadMember(): Member | null {
  // Only load member if we also have a token (server-side session required)
  const token = getToken();
  if (!token) return null;

  // Try localStorage first
  const stored = localStorage.getItem(STORAGE_KEY);
  if (stored) {
    try {
      return JSON.parse(stored);
    } catch { /* fall through */ }
  }

  // Fall back to cookie (survives localStorage being cleared)
  const cookie = getCookie(COOKIE_KEY);
  if (cookie) {
    try {
      const member = JSON.parse(cookie);
      // Restore localStorage from cookie
      localStorage.setItem(STORAGE_KEY, cookie);
      return member;
    } catch { /* fall through */ }
  }

  return null;
}

export const AuthProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [member, setMember] = useState<Member | null>(loadMember);

  const login = (memberData: Member, token: string) => {
    setMember(memberData);
    setToken(token);
    const json = JSON.stringify(memberData);
    localStorage.setItem(STORAGE_KEY, json);
    setCookie(COOKIE_KEY, json);
  };

  const logout = () => {
    // Tell the server to destroy the session
    apiCall('/api/auth/logout', { method: 'POST' }).catch(() => {});
    setMember(null);
    clearToken();
    localStorage.removeItem(STORAGE_KEY);
    localStorage.removeItem('isAdmin');
    deleteCookie(COOKIE_KEY);
  };

  // Refresh the cached member from the server once on load. The copy in
  // localStorage is frozen at login time, so without this an admin change
  // (landing page, instrument, admin flag) wouldn't apply until re-login.
  // Left deliberately quiet: a network failure keeps the cached member, and
  // apiCall already handles a 401 by clearing the session.
  useEffect(() => {
    if (!getToken()) return;
    let cancelled = false;
    apiCall('/api/auth/me')
      .then(res => (res.ok ? res.json() : null))
      .then(data => {
        if (cancelled || !data?.member) return;
        setMember(data.member);
        const json = JSON.stringify(data.member);
        localStorage.setItem(STORAGE_KEY, json);
        setCookie(COOKIE_KEY, json);
      })
      .catch(() => { /* offline or server blip - keep the cached member */ });
    return () => { cancelled = true; };
  }, []);

  // Listen for changes in other tabs
  useEffect(() => {
    const handler = (e: StorageEvent) => {
      if (e.key === STORAGE_KEY) {
        if (e.newValue) {
          try {
            setMember(JSON.parse(e.newValue));
          } catch {
            setMember(null);
          }
        } else {
          setMember(null);
        }
      }
    };
    window.addEventListener('storage', handler);
    return () => window.removeEventListener('storage', handler);
  }, []);

  return (
    <AuthContext.Provider value={{
      member,
      isLoggedIn: member !== null,
      isAdmin: member?.isAdmin || false,
      landingPage: landingPageFor(member),
      login,
      logout
    }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
